import { useState, useMemo } from "react";
import { fmt } from "../utils/fmt";
import {
  ECHO_CARDS, RARITY, BOOST_COST, COLLECTION_MILESTONES,
  MAX_CARD_LEVEL,
} from "../data/echoCards";
import { costToLevelUp, isAbilityUnlocked, cardStatContribution } from "../hooks/useEchoCards";

const CATEGORY_LABELS = {
  mining: "⛏️ Mining",
  forge:  "🔥 Forge",
  combat: "⚔️ Combat",
  echo:   "🌀 Echo & Rift",
  trade:  "💹 Trade",
  exploration: "🗺️ Exploration",
};

const CATEGORIES = ["mining", "forge", "combat", "echo", "trade", "exploration"];

export function EchoCardsTab({
  echoCurrency, voidShards,
  ownedCards, equippedCards, boostedCards,
  milestoneMultipliers,
  levelUpCard, boostCard, toggleEquipped,
  stats,
}) {
  const maxSlots = stats?.activeCardSlots ?? 1;
  const [selectedId, setSelectedId] = useState(null);
  const [filterCat, setFilterCat]   = useState("all");
  const [filterOwned, setFilterOwned] = useState("all"); // "all" | "owned" | "unowned"

  const ownedCount = Object.keys(ownedCards).length;
  const nextMilestone = COLLECTION_MILESTONES.find(m => ownedCount < m.cards) ?? null;

  const filtered = useMemo(() => {
    return ECHO_CARDS.filter(card => {
      if (filterCat !== "all" && card.category !== filterCat) return false;
      const owned = ownedCards[card.id] != null;
      if (filterOwned === "owned"   && !owned) return false;
      if (filterOwned === "unowned" && owned)  return false;
      return true;
    });
  }, [filterCat, filterOwned, ownedCards]);

  const selectedCard   = selectedId ? ECHO_CARDS.find(c => c.id === selectedId) : null;
  const selectedLevel  = selectedCard ? (ownedCards[selectedCard.id] ?? 0) : 0;
  const selectedBoosted = selectedCard ? boostedCards.has(selectedCard.id) : false;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>

      {/* Header bar */}
      <div style={{ padding: "10px 14px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 1, color: "#bb77ff", fontWeight: 700 }}>RIFT ECHOES</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>{ownedCount} / {ECHO_CARDS.length} collected</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <CurrencyPill icon="〰️" label="Echo" value={Math.floor(echoCurrency)} color="#bb77ff" />
            <CurrencyPill icon="◈"  label="Void Shards" value={Math.floor(voidShards)} color="#ffcc22" />
          </div>
        </div>

        {/* Collection progress + milestones */}
        <CollectionBar ownedCount={ownedCount} nextMilestone={nextMilestone} milestoneMultipliers={milestoneMultipliers} />

        {/* Equipped slots */}
        <EquippedRow
          equippedCards={equippedCards}
          ownedCards={ownedCards}
          boostedCards={boostedCards}
          maxSlots={maxSlots}
          onTap={setSelectedId}
        />

        {/* Filter row */}
        <div style={{ display: "flex", gap: 4, marginTop: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <FilterPill active={filterCat === "all"} onClick={() => setFilterCat("all")} label="All" />
          {CATEGORIES.map(cat => (
            <FilterPill key={cat} active={filterCat === cat} onClick={() => setFilterCat(cat)} label={CATEGORY_LABELS[cat]} />
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            <FilterPill active={filterOwned === "all"}     onClick={() => setFilterOwned("all")}     label="All" dim />
            <FilterPill active={filterOwned === "owned"}   onClick={() => setFilterOwned("owned")}   label="Owned" dim />
            <FilterPill active={filterOwned === "unowned"} onClick={() => setFilterOwned("unowned")} label="New" dim />
          </div>
        </div>
      </div>

      {/* Card grid */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px 14px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
          {filtered.map(card => {
            const level = ownedCards[card.id] ?? 0;
            const owned = level > 0;
            const equipped = equippedCards.includes(card.id);
            const boosted = boostedCards.has(card.id);
            return (
              <MiniCard
                key={card.id}
                card={card}
                level={level}
                owned={owned}
                equipped={equipped}
                boosted={boosted}
                onTap={() => setSelectedId(card.id)}
              />
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.25)", fontSize: 12 }}>
            No Rift Echoes match this filter.
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedCard && (
        <CardDetailPanel
          card={selectedCard}
          level={selectedLevel}
          boosted={selectedBoosted}
          equipped={equippedCards.includes(selectedCard.id)}
          canEquip={!equippedCards.includes(selectedCard.id) && equippedCards.length < maxSlots}
          echoCurrency={echoCurrency}
          voidShards={voidShards}
          onLevelUp={() => levelUpCard(selectedCard.id)}
          onBoost={() => boostCard(selectedCard.id)}
          onToggleEquip={() => toggleEquipped(selectedCard.id, maxSlots)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ── Equipped row ──────────────────────────────────────────────────────────────
function EquippedRow({ equippedCards, ownedCards, boostedCards, maxSlots, onTap }) {
  return (
    <div style={{ display: "flex", gap: 5, alignItems: "center", padding: "7px 10px", background: "rgba(187,119,255,0.06)", border: "1px solid rgba(187,119,255,0.18)", borderRadius: 8, marginTop: 4 }}>
      <span style={{ fontSize: 9, color: "rgba(187,119,255,0.8)", fontWeight: 700, letterSpacing: 1, marginRight: 2, flexShrink: 0 }}>EQUIPPED</span>
      {Array.from({ length: maxSlots }).map((_, i) => {
        const cardId = equippedCards[i];
        const card   = cardId ? ECHO_CARDS.find(c => c.id === cardId) : null;
        const level  = card ? (ownedCards[cardId] ?? 0) : 0;
        const boosted = card ? boostedCards.has(cardId) : false;
        const rarityColor = card ? RARITY[card.rarity]?.border : "rgba(255,255,255,0.1)";
        return (
          <div
            key={i}
            onClick={() => card && onTap(cardId)}
            style={{ width: 40, height: 40, borderRadius: 7, background: card ? `${rarityColor}18` : "rgba(255,255,255,0.02)", border: `1px solid ${card ? rarityColor : "rgba(255,255,255,0.1)"}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: card ? "pointer" : "default", flexShrink: 0, gap: 1 }}
          >
            {card ? (
              <>
                <span style={{ fontSize: 16 }}>{card.icon}</span>
                <span style={{ fontSize: 7, color: rarityColor, fontWeight: 700 }}>Lv{level}{boosted ? "✦" : ""}</span>
              </>
            ) : (
              <span style={{ fontSize: 14, opacity: 0.15 }}>◇</span>
            )}
          </div>
        );
      })}
      {maxSlots < 6 && (
        <span style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", marginLeft: 4 }}>Research for more slots</span>
      )}
    </div>
  );
}

// ── Collection progress bar ───────────────────────────────────────────────────
function CollectionBar({ ownedCount, nextMilestone, milestoneMultipliers }) {
  const pct = (ownedCount / 50) * 100;
  const allMult = ((milestoneMultipliers?.all ?? 1) - 1) * 100;
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "7px 10px", marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", fontWeight: 700, letterSpacing: 0.5 }}>COLLECTION</span>
        <span style={{ fontSize: 9, color: "#bb77ff", fontWeight: 700 }}>
          {allMult > 0 ? `+${allMult.toFixed(0)}% all stats active` : ""}
          {nextMilestone ? ` — next milestone: ${nextMilestone.cards} echoes` : " — FULL COLLECTION!"}
        </span>
      </div>
      <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, #bb77ff, #ffcc22)", borderRadius: 2, transition: "width 0.4s" }} />
      </div>
      {/* Milestone tick marks */}
      <div style={{ position: "relative", height: 8, marginTop: 2 }}>
        {COLLECTION_MILESTONES.map(m => (
          <div key={m.cards} style={{ position: "absolute", left: `${(m.cards / 50) * 100}%`, transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: 1, height: 4, background: ownedCount >= m.cards ? "#ffcc22" : "rgba(255,255,255,0.12)" }} />
            <span style={{ fontSize: 6, color: ownedCount >= m.cards ? "#ffcc22" : "rgba(255,255,255,0.2)", fontWeight: 700 }}>{m.cards}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Mini card tile ────────────────────────────────────────────────────────────
function MiniCard({ card, level, owned, equipped, boosted, onTap }) {
  const rarityColor = RARITY[card.rarity]?.border ?? "#888";
  const pct = (level / MAX_CARD_LEVEL) * 100;
  return (
    <div
      onClick={onTap}
      style={{
        borderRadius: 8,
        background: owned ? (equipped ? `${rarityColor}20` : "rgba(255,255,255,0.04)") : "rgba(255,255,255,0.015)",
        border: `1px solid ${owned ? (equipped ? rarityColor : `${rarityColor}55`) : "rgba(255,255,255,0.06)"}`,
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: "7px 4px 5px", gap: 2, cursor: "pointer", userSelect: "none",
        opacity: owned ? 1 : 0.38,
        transition: "border-color 0.15s",
        position: "relative",
      }}
    >
      {equipped && (
        <div style={{ position: "absolute", top: 3, right: 3, width: 4, height: 4, borderRadius: "50%", background: rarityColor, boxShadow: `0 0 4px ${rarityColor}` }} />
      )}
      {boosted && owned && (
        <div style={{ position: "absolute", top: 3, left: 3, fontSize: 7, color: "#ffcc22" }}>✦</div>
      )}
      <span style={{ fontSize: 18, filter: owned ? "none" : "grayscale(1) brightness(0.4)" }}>{card.icon}</span>
      <span style={{ fontSize: 7, fontWeight: 700, color: owned ? rarityColor : "rgba(255,255,255,0.15)", letterSpacing: 0.3 }}>
        {owned ? `Lv${level}/${MAX_CARD_LEVEL}` : "???"}
      </span>
      <div style={{ width: "80%", height: 2, background: "rgba(255,255,255,0.06)", borderRadius: 1 }}>
        {owned && <div style={{ height: "100%", width: `${pct}%`, background: level >= MAX_CARD_LEVEL ? "#66dd88" : rarityColor, borderRadius: 1 }} />}
      </div>
    </div>
  );
}

// ── Card detail panel (slide-up sheet) ───────────────────────────────────────
function CardDetailPanel({ card, level, boosted, equipped, canEquip, echoCurrency, voidShards, onLevelUp, onBoost, onToggleEquip, onClose }) {
  const rarityColor  = RARITY[card.rarity]?.border ?? "#888";
  const rarityCfg    = RARITY[card.rarity];
  const owned        = level > 0;
  const atMax        = level >= MAX_CARD_LEVEL;
  const nextLevelCost = owned && !atMax ? costToLevelUp(level) : null;
  const canAfford    = nextLevelCost != null && echoCurrency >= nextLevelCost;
  const boostCost    = BOOST_COST[card.rarity] ?? 5;
  const canAffordBoost = voidShards >= boostCost;
  const ab1Unlocked  = isAbilityUnlocked(card, 0, level);
  const ab2Unlocked  = isAbilityUnlocked(card, 1, level);
  const ab1UnlockLv  = rarityCfg?.abilityLv[0] ?? 10;
  const ab2UnlockLv  = rarityCfg?.abilityLv[1] ?? 20;

  // Stat contribution at current level
  const contrib = cardStatContribution(card, level, boosted);
  const statVal  = Object.values(contrib)[0] ?? 0;

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.78)", display: "flex", alignItems: "flex-end", zIndex: 50, backdropFilter: "blur(2px)" }}
    >
      <div style={{ width: "100%", maxHeight: "90%", overflowY: "auto", background: "#080c18", borderTop: `2px solid ${owned ? rarityColor : "rgba(255,255,255,0.12)"}`, borderRadius: "14px 14px 0 0", padding: "14px 14px 20px", boxShadow: `0 -8px 40px ${owned ? rarityColor + "22" : "transparent"}` }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 50, height: 50, borderRadius: 10, background: owned ? `${rarityColor}18` : "rgba(255,255,255,0.03)", border: `2px solid ${owned ? rarityColor + "77" : "rgba(255,255,255,0.1)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, filter: owned ? "none" : "grayscale(1) brightness(0.5)" }}>
            {card.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: owned ? rarityColor : "rgba(255,255,255,0.4)" }}>{owned ? card.name : "???"}</span>
              <RarityBadge rarity={card.rarity} />
              {boosted && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "rgba(255,204,34,0.15)", color: "#ffcc22", fontWeight: 700 }}>✦ BOOSTED</span>}
              {equipped && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "rgba(187,119,255,0.15)", color: "#bb77ff", fontWeight: 700 }}>EQUIPPED</span>}
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
              {owned ? `${CATEGORY_LABELS[card.category]} · ${card.statLabel}` : card.acquireDesc}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "rgba(255,255,255,0.5)", width: 28, height: 28, cursor: "pointer", fontFamily: "inherit", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
        </div>

        {/* Acquire hint for unowned */}
        {!owned && (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 11, color: "rgba(255,255,255,0.45)" }}>
            <span style={{ color: "rgba(255,255,255,0.25)", fontWeight: 700, fontSize: 9, letterSpacing: 0.5, display: "block", marginBottom: 3 }}>HOW TO OBTAIN</span>
            {card.acquireDesc}
          </div>
        )}

        {/* Level bar */}
        {owned && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", fontWeight: 700, letterSpacing: 0.5 }}>LEVEL</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: atMax ? "#66dd88" : rarityColor }}>{atMax ? "MAX" : `${level} / ${MAX_CARD_LEVEL}`}</span>
            </div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <div style={{ height: "100%", width: `${(level / MAX_CARD_LEVEL) * 100}%`, background: atMax ? "#66dd88" : rarityColor, borderRadius: 2, transition: "width 0.3s" }} />
            </div>
          </div>
        )}

        {/* Stat display */}
        {owned && (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>STAT BONUS AT LV{level}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: rarityColor }}>
              +{statVal}{card.stat === "playerMaxHp" || card.stat === "shieldMax" || card.stat === "bonusLoot" ? "" : "%"} {card.statLabel}
              {boosted && <span style={{ fontSize: 10, color: "#ffcc22", marginLeft: 6 }}>(×1.5 boosted)</span>}
            </div>
          </div>
        )}

        {/* Abilities */}
        {owned && (
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <AbilityPill
              label={`Ability 1 · Lv${ab1UnlockLv}`}
              desc={card.ability1?.desc}
              unlocked={ab1Unlocked}
              color={rarityColor}
            />
            <AbilityPill
              label={`Ability 2 · Lv${ab2UnlockLv}`}
              desc={card.ability2?.desc}
              unlocked={ab2Unlocked}
              color={rarityColor}
            />
          </div>
        )}

        {/* Level-up cost / next level preview */}
        {owned && nextLevelCost != null && (
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "7px 10px", marginBottom: 10, fontSize: 10, color: "rgba(255,255,255,0.45)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Lv{level} → Lv{level + 1}</span>
            <span style={{ color: canAfford ? "#bb77ff" : "rgba(255,100,100,0.8)", fontWeight: 700 }}>
              〰️ {fmt(nextLevelCost)} Echo Currency
              {!canAfford && <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.6 }}>(need {fmt(nextLevelCost - echoCurrency)} more)</span>}
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 7 }}>
          {/* Level-up */}
          {owned && !atMax && (
            <button
              onClick={onLevelUp}
              disabled={!canAfford}
              style={{ flex: 1, padding: "11px 0", background: canAfford ? `${rarityColor}20` : "rgba(255,255,255,0.03)", border: `1px solid ${canAfford ? rarityColor : "rgba(255,255,255,0.1)"}`, borderRadius: 9, color: canAfford ? rarityColor : "rgba(255,255,255,0.25)", fontFamily: "inherit", fontWeight: 700, fontSize: 13, cursor: canAfford ? "pointer" : "not-allowed" }}
            >
              LEVEL UP
            </button>
          )}
          {owned && atMax && (
            <div style={{ flex: 1, padding: "11px 0", textAlign: "center", background: "rgba(102,221,136,0.08)", border: "1px solid rgba(102,221,136,0.3)", borderRadius: 9, color: "#66dd88", fontWeight: 700, fontSize: 13 }}>
              MAX LEVEL ✓
            </div>
          )}
          {/* Boost */}
          {owned && !boosted && (
            <button
              onClick={onBoost}
              disabled={!canAffordBoost}
              title={`Boost with ${boostCost} ◈ Void Shards — grants permanent +50% to all stat output`}
              style={{ padding: "11px 14px", background: canAffordBoost ? "rgba(255,204,34,0.1)" : "rgba(255,255,255,0.02)", border: `1px solid ${canAffordBoost ? "#ffcc22" : "rgba(255,255,255,0.1)"}`, borderRadius: 9, color: canAffordBoost ? "#ffcc22" : "rgba(255,255,255,0.25)", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: canAffordBoost ? "pointer" : "not-allowed", flexShrink: 0 }}
            >
              ◈{boostCost} BOOST
            </button>
          )}
          {/* Equip/unequip */}
          {owned && (
            equipped ? (
              <button
                onClick={onToggleEquip}
                style={{ padding: "11px 14px", background: "rgba(187,119,255,0.08)", border: "1px solid rgba(187,119,255,0.4)", borderRadius: 9, color: "#bb77ff", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer", flexShrink: 0 }}
              >
                UNEQUIP
              </button>
            ) : canEquip ? (
              <button
                onClick={onToggleEquip}
                style={{ padding: "11px 14px", background: `${rarityColor}12`, border: `1px solid ${rarityColor}`, borderRadius: 9, color: rarityColor, fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer", flexShrink: 0 }}
              >
                EQUIP
              </button>
            ) : (
              <div style={{ padding: "11px 14px", textAlign: "center", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, color: "rgba(255,255,255,0.25)", fontSize: 11, flexShrink: 0 }}>
                Slots full
              </div>
            )
          )}
        </div>

      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function RarityBadge({ rarity }) {
  const cfg = RARITY[rarity];
  return (
    <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: `${cfg?.border}22`, color: cfg?.border, fontWeight: 700, letterSpacing: 0.5, border: `1px solid ${cfg?.border}44` }}>
      {cfg?.label?.toUpperCase()}
    </span>
  );
}

function AbilityPill({ label, desc, unlocked, color }) {
  return (
    <div style={{ flex: 1, padding: "5px 8px", borderRadius: 7, background: unlocked ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.01)", border: `1px solid ${unlocked ? color + "55" : "rgba(255,255,255,0.06)"}`, opacity: unlocked ? 1 : 0.45 }}>
      <div style={{ fontSize: 8, color: unlocked ? color : "rgba(255,255,255,0.25)", fontWeight: 700, letterSpacing: 0.8, marginBottom: 3 }}>
        {unlocked ? "✓ " : "🔒 "}{label}
      </div>
      <div style={{ fontSize: 10, color: unlocked ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.3)", lineHeight: 1.35 }}>{desc}</div>
    </div>
  );
}

function CurrencyPill({ icon, label, value, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", background: `${color}11`, border: `1px solid ${color}33`, borderRadius: 6 }}>
      <span style={{ fontSize: 11 }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color }}>{fmt(value)}</span>
    </div>
  );
}

function FilterPill({ active, onClick, label, dim }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${active ? (dim ? "rgba(255,255,255,0.3)" : "rgba(187,119,255,0.6)") : "rgba(255,255,255,0.1)"}`, background: active ? (dim ? "rgba(255,255,255,0.08)" : "rgba(187,119,255,0.12)") : "transparent", color: active ? (dim ? "rgba(255,255,255,0.8)" : "#bb77ff") : "rgba(255,255,255,0.35)", fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.5 }}
    >
      {label}
    </button>
  );
}
